import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileCheck, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function ProspectDisclosure() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    sport: "",
    highSchool: "",
    expectedEnrollmentDate: "",
    currentDeals: "",
    totalNilEarnings: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trackingCode, setTrackingCode] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Parse deals
      const deals = formData.currentDeals
        ? formData.currentDeals.split("\n").map((line) => line.trim()).filter((line) => line)
        : [];

      // Insert prospect disclosure
      const { data, error } = await supabase
        .from("recruitment_tracking")
        .insert({
          prospect_name: formData.fullName,
          sport: formData.sport,
          contact_date: new Date().toISOString(),
          contact_type: "disclosure_form",
          prospect_email: formData.email,
          prospect_phone: formData.phone,
          graduation_year: formData.expectedEnrollmentDate.substring(0, 4),
          pre_enrollment_deals: deals.map((deal) => ({
            description: deal,
            disclosed_at: new Date().toISOString(),
          })),
          notes: `High School: ${formData.highSchool}, Total NIL Earnings: $${formData.totalNilEarnings}`,
        })
        .select()
        .single();

      if (error) throw error;

      const code = data.id.substring(0, 8).toUpperCase();
      setTrackingCode(code);
      setSubmitted(true);
      
      toast.success("Disclosure submitted successfully", {
        description: `Your tracking code is: ${code}`,
      });

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        sport: "",
        highSchool: "",
        expectedEnrollmentDate: "",
        currentDeals: "",
        totalNilEarnings: "",
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit disclosure", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
              <FileCheck className="h-6 w-6 text-success" />
            </div>
            <CardTitle>Disclosure Submitted</CardTitle>
            <CardDescription>
              Thank you for submitting your pre-enrollment NIL disclosure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-accent rounded-lg">
              <p className="text-sm font-medium mb-1">Your Tracking Code</p>
              <p className="text-2xl font-bold text-center">{trackingCode}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Save this code to check your submission status
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Your institution's compliance office will review your disclosure and contact you
              if additional information is needed.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setSubmitted(false)}
            >
              Submit Another Disclosure
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 bg-background">
      <div className="max-w-3xl mx-auto space-y-6 py-8">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Pre-Enrollment NIL Disclosure</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            High school and transfer student-athletes: Disclose your existing NIL deals before enrollment.
            This is required by NCAA rules and helps ensure compliance from day one.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Prospect Information</CardTitle>
            <CardDescription>
              All fields are required for compliance tracking
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sport">Sport *</Label>
                  <Input
                    id="sport"
                    required
                    value={formData.sport}
                    onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="highSchool">Current High School/Institution *</Label>
                  <Input
                    id="highSchool"
                    required
                    value={formData.highSchool}
                    onChange={(e) => setFormData({ ...formData, highSchool: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expectedEnrollmentDate">Expected Enrollment Date *</Label>
                  <Input
                    id="expectedEnrollmentDate"
                    type="date"
                    required
                    value={formData.expectedEnrollmentDate}
                    onChange={(e) => setFormData({ ...formData, expectedEnrollmentDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="totalNilEarnings">Total NIL Earnings to Date ($) *</Label>
                <Input
                  id="totalNilEarnings"
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.totalNilEarnings}
                  onChange={(e) => setFormData({ ...formData, totalNilEarnings: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currentDeals">Current NIL Deals *</Label>
                <Textarea
                  id="currentDeals"
                  required
                  rows={6}
                  placeholder="List each NIL deal on a new line. Include: Brand name, deal type, compensation amount, and dates.&#10;&#10;Example:&#10;Nike - Social media promotion - $2,500 - Active since Jan 2025&#10;Local Pizza Shop - Endorsement - $500/month - Ongoing"
                  value={formData.currentDeals}
                  onChange={(e) => setFormData({ ...formData, currentDeals: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Provide details for each active NIL agreement
                </p>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                <Upload className="mr-2 h-4 w-4" />
                {isSubmitting ? "Submitting..." : "Submit Disclosure"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                By submitting, you certify that the information provided is accurate and complete.
                False disclosure may result in eligibility issues.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}